import { useState } from 'react'
import type { CachedAuftrag } from '@/lib/db'
import { getDevice } from '@/lib/db'
import { submitSignatur, abschliessenAuftrag } from '@/lib/api'
import { SignatureCanvas } from '@/components/SignatureCanvas'

interface Props {
  auftrag: CachedAuftrag
  onDone: () => void
  onCancel: () => void
}

type Step = 'auftragnehmer' | 'auftraggeber' | 'fertig'

/** Auftragsabschluss: erst der Glasbrenner-Mitarbeiter (Auftragnehmer) unterschreibt,
 * DANACH wird das Tablet an den Kunden uebergeben (Auftraggeber) -- Reihenfolge ist
 * Patric-Entscheid 07.09.2026. Die Auftraggeber-Unterschrift ist ueberspringbar
 * (Patric-Entscheid nach O-Ton Larissa: bei Subs/Firmen eher unueblich, SPEC §4.7). */
export function AuftragAbschlussPage({ auftrag, onDone, onCancel }: Props) {
  const [step, setStep] = useState<Step>('auftragnehmer')
  const [auftragnehmerName, setAuftragnehmerName] = useState('')
  const [auftraggeberName, setAuftraggeberName] = useState(auftrag.auftraggeberName)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(unterschriebenVon: 'auftragnehmer' | 'auftraggeber', pngBase64: string) {
    setBusy(true)
    setError(null)
    try {
      const device = await getDevice()
      if (!device) throw new Error('Geraet ist nicht enrollt')

      const name = unterschriebenVon === 'auftragnehmer' ? auftragnehmerName : auftraggeberName
      if (!name.trim()) throw new Error('Name fehlt')

      await submitSignatur({
        deviceId: device.deviceId,
        auftragId: auftrag.auftragId,
        unterschriebenVon,
        unterzeichnerName: name.trim(),
        signaturPngBase64: pngBase64,
      })

      if (unterschriebenVon === 'auftragnehmer') {
        setStep('auftraggeber')
      } else {
        setStep('fertig')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function skipAuftraggeber() {
    setBusy(true)
    setError(null)
    try {
      const device = await getDevice()
      if (!device) throw new Error('Geraet ist nicht enrollt')
      await abschliessenAuftrag(device.deviceId, auftrag.auftragId)
      setStep('fertig')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="screen">
      <div className="card">
        <h1>Auftrag abschließen</h1>
        <p>
          {auftrag.auftragsnummer} — {auftrag.auftraggeberName}
        </p>
        {error && <p className="error-text">{error}</p>}

        {step === 'auftragnehmer' && (
          <>
            <input
              className="input"
              placeholder="Name Mitarbeiter"
              value={auftragnehmerName}
              onChange={(e) => setAuftragnehmerName(e.target.value)}
            />
            <SignatureCanvas
              label="Unterschrift Auftragnehmer (Mitarbeiter)"
              onDone={(png) => submit('auftragnehmer', png)}
            />
          </>
        )}

        {step === 'auftraggeber' && (
          <>
            <p>Tablet bitte jetzt dem Kunden übergeben.</p>
            <input
              className="input"
              placeholder="Name Auftraggeber"
              value={auftraggeberName}
              onChange={(e) => setAuftraggeberName(e.target.value)}
            />
            <SignatureCanvas
              label="Unterschrift Auftraggeber (Kunde)"
              onDone={(png) => submit('auftraggeber', png)}
            />
            <button
              className="button button-secondary"
              onClick={skipAuftraggeber}
              disabled={busy}
            >
              Unterschrift überspringen
            </button>
          </>
        )}

        {step === 'fertig' && (
          <>
            <p className="confirmation">Auftrag abgeschlossen.</p>
            <button className="button button-in" onClick={onDone}>
              Zurück zum Stempeln
            </button>
          </>
        )}

        {step !== 'fertig' && (
          <button className="button button-secondary" onClick={onCancel} disabled={busy}>
            Abbrechen
          </button>
        )}
      </div>
    </div>
  )
}
